'use client';

import { motion, useInView } from 'motion/react';
import { useRef, useState } from 'react';
import {
  Layers,
  ChevronRight,
  Shield,
  Lock,
  Eye,
  RefreshCcw,
} from 'lucide-react';

const frameworkData = {
  objectives: [
    {
      id: 'A',
      name: 'Managing Security Risk',
      icon: Shield,
      color: 'hsl(168 100% 45%)',
      bgColor: 'bg-accent/10',
      textColor: 'text-accent',
      principles: [
        { id: 'A1', name: 'Governance', cos: ['A1.a', 'A1.b', 'A1.c'] },
        { id: 'A2', name: 'Risk Management', cos: ['A2.a', 'A2.b'] },
        { id: 'A3', name: 'Asset Management', cos: ['A3.a', 'A3.b'] },
        { id: 'A4', name: 'Supply Chain', cos: ['A4.a', 'A4.b'] },
      ],
    },
    {
      id: 'B',
      name: 'Protecting Against Cyber Attack',
      icon: Lock,
      color: 'hsl(225 60% 45%)',
      bgColor: 'bg-[hsl(225_60%_45%/0.1)]',
      textColor: 'text-[hsl(225_60%_45%)]',
      principles: [
        { id: 'B1', name: 'Service Protection', cos: ['B1.a', 'B1.b'] },
        { id: 'B2', name: 'Identity & Access', cos: ['B2.a', 'B2.b', 'B2.c', 'B2.d'] },
        { id: 'B3', name: 'Data Security', cos: ['B3.a', 'B3.b'] },
        { id: 'B4', name: 'System Security', cos: ['B4.a', 'B4.b', 'B4.c'] },
        { id: 'B5', name: 'Resilient Networks', cos: ['B5.a', 'B5.b'] },
        { id: 'B6', name: 'Staff Awareness', cos: ['B6.a'] },
      ],
    },
    {
      id: 'C',
      name: 'Detecting Cyber Security Events',
      icon: Eye,
      color: 'hsl(38 92% 50%)',
      bgColor: 'bg-warning/10',
      textColor: 'text-warning',
      principles: [
        { id: 'C1', name: 'Security Monitoring', cos: ['C1.a', 'C1.b', 'C1.c'] },
        { id: 'C2', name: 'Proactive Detection', cos: ['C2.a', 'C2.b'] },
      ],
    },
    {
      id: 'D',
      name: 'Minimising Impact',
      icon: RefreshCcw,
      color: 'hsl(160 84% 39%)',
      bgColor: 'bg-success/10',
      textColor: 'text-success',
      principles: [
        { id: 'D1', name: 'Response & Recovery', cos: ['D1.a', 'D1.b', 'D1.c'] },
        { id: 'D2', name: 'Lessons Learned', cos: ['D2.a', 'D2.b'] },
      ],
    },
  ],
};

export function FrameworkSection() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });
  const [selectedObjective, setSelectedObjective] = useState<string>('A');

  const activeObjective = frameworkData.objectives.find(
    (o) => o.id === selectedObjective
  );

  return (
    <section
      className="relative overflow-hidden bg-primary py-24 lg:py-32"
      id="framework"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="bg-grid-dense h-full w-full" />
      </div>
      <div className="absolute left-0 top-0 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/10 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-[400px] w-[400px] translate-x-1/2 translate-y-1/2 rounded-full bg-accent/5 blur-3xl" />

      <div
        ref={containerRef}
        className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
      >
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-3xl text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-1.5"
          >
            <Layers className="h-4 w-4 text-accent" />
            <span className="font-heading text-sm font-medium text-accent">
              CAF Framework
            </span>
          </motion.div>

          <h2 className="font-display text-3xl tracking-tight-display text-primary-foreground sm:text-4xl lg:text-5xl">
            Built around the{' '}
            <span className="italic text-accent">NCSC CAF structure</span>
          </h2>

          <p className="mt-6 text-lg text-primary-foreground/70">
            The full CAF 4.0 hierarchy is built directly into the platform —
            every Objective, Principle, Contributing Outcome, and IGP organised
            in one navigable structure.
          </p>
        </motion.div>

        {/* Framework Visualization */}
        <div className="mt-16">
          {/* Objective Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-3"
          >
            {frameworkData.objectives.map((obj) => {
              const Icon = obj.icon;
              const isSelected = selectedObjective === obj.id;

              return (
                <button
                  key={obj.id}
                  onClick={() => setSelectedObjective(obj.id)}
                  className={`group flex items-center gap-3 rounded-xl border px-5 py-3 transition-all duration-300 ${
                    isSelected
                      ? 'border-accent bg-accent text-accent-foreground shadow-lg'
                      : 'border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground/80 hover:border-accent/50 hover:bg-accent/10'
                  }`}
                >
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      isSelected ? 'bg-accent-foreground/10' : 'bg-primary-foreground/10'
                    }`}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div className="text-left">
                    <p className="font-heading text-sm font-bold">Objective {obj.id}</p>
                    <p
                      className={`text-xs ${
                        isSelected
                          ? 'text-accent-foreground/80'
                          : 'text-primary-foreground/60'
                      }`}
                    >
                      {obj.name}
                    </p>
                  </div>
                </button>
              );
            })}
          </motion.div>

          {/* Active Objective Detail */}
          {activeObjective && (
            <motion.div
              key={activeObjective.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-10"
            >
              <div className="rounded-2xl border border-primary-foreground/10 bg-primary-foreground/5 p-6 backdrop-blur-sm lg:p-8">
                {/* Principles Grid */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {activeObjective.principles.map((principle, index) => (
                    <motion.div
                      key={principle.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="group rounded-xl border border-primary-foreground/10 bg-primary-foreground/5 p-5 transition-all duration-300 hover:border-accent/30 hover:bg-accent/5"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-bold ${activeObjective.bgColor} ${activeObjective.textColor}`}
                          >
                            {principle.id}
                          </span>
                          <h4 className="mt-2 font-heading text-base font-semibold text-primary-foreground">
                            {principle.name}
                          </h4>
                        </div>
                        <ChevronRight className="h-4 w-4 text-primary-foreground/30 transition-all duration-300 group-hover:translate-x-1 group-hover:text-accent" />
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {principle.cos.map((co) => (
                          <span
                            key={co}
                            className="rounded-md border border-primary-foreground/10 bg-primary-foreground/5 px-2 py-1 font-mono text-xs text-primary-foreground/70"
                          >
                            {co}
                          </span>
                        ))}
                      </div>

                      <p className="mt-3 text-xs text-primary-foreground/50">
                        {principle.cos.length} Contributing Outcome{principle.cos.length !== 1 ? 's' : ''}
                      </p>
                    </motion.div>
                  ))}
                </div>

                {/* Stats */}
                <div className="mt-8 flex flex-wrap items-center justify-center gap-8 border-t border-primary-foreground/10 pt-8">
                  {[
                    { value: '4', label: 'Objectives' },
                    { value: '14', label: 'Principles' },
                    { value: '41', label: 'Contributing Outcomes' },
                    { value: '~300', label: 'IGPs' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <p className="font-display text-3xl text-accent">{stat.value}</p>
                      <p className="text-xs uppercase tracking-wide-caps text-primary-foreground/50">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Hierarchy visualization */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-4"
          >
            {['Objectives', 'Principles', 'Contributing Outcomes', 'IGPs'].map(
              (level, index) => (
                <div key={level} className="flex items-center gap-4">
                  <div className="flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-4 py-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{
                        backgroundColor:
                          index === 0
                            ? 'hsl(168 100% 45%)'
                            : index === 1
                              ? 'hsl(225 60% 45%)'
                              : index === 2
                                ? 'hsl(38 92% 50%)'
                                : 'hsl(160 84% 39%)',
                      }}
                    />
                    <span className="font-heading text-sm font-medium text-primary-foreground">
                      {level}
                    </span>
                  </div>
                  {index < 3 && (
                    <ChevronRight className="h-4 w-4 text-primary-foreground/30" />
                  )}
                </div>
              )
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
